/**
 * @file src/components/molecules/CopyButton/CopyButton.tsx
 * @description A button component with copy functionality, optionally displaying labels and a display value.
 * @module CopyButton
 */

import React, { FC, useState } from 'react';
import { Button, Text } from '@/components';
import { IconsName } from '@/components';
import { IconVariants } from '@/interfaces';

interface CopyButtonProps {
  value: string;
  iconSize?: IconVariants['size'];
  showLabel?: boolean;
  labelCopied?: React.ReactNode;
  labelCopy?: React.ReactNode;
  showDisplayValue?: boolean;
  displayValue?: string;
  displayValueSize?: string;
  displayValueColor?: string;
  disabled?: boolean;
  onClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
}

const spacingConfig = {
  rootGap: 'gap-2xs',
};

const CopyButton: FC<CopyButtonProps> = ({
  iconSize = 'sm',
  showLabel = true,
  showDisplayValue = true,
  labelCopied = 'Copied',
  labelCopy = 'Copy',
  value,
  displayValue = '',
  displayValueSize = 'base',
  displayValueColor = 'primary',
  disabled,
  onClick,
}) => {
  const [isCopied, setIsCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 3000); // Reset copied state after 3 seconds
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  // Determine which icon to display based on copy state
  const iconToUse: IconsName = isCopied ? 'checkMark' : 'copy';

  return (
    <div className={`flex flex-row items-center ${spacingConfig.rootGap}`}>
      {showDisplayValue && (
        <Text
          value={displayValue}
          size={displayValueSize as any}
          color={displayValueColor as any}
        />
      )}
      <Button
        intent="ghost"
        iconStyles={{ intent: 'accent', size: iconSize as any }}
        disabled={disabled}
        onClick={(e) => {
          handleCopy();
          if (onClick) onClick(e);
        }}
        icon={iconToUse}
        flexHeight
      >
        {showLabel ? (isCopied ? labelCopied : labelCopy) : null}
      </Button>
    </div>
  );
};

export default CopyButton;
